import { Copy, ExternalLink } from "lucide-react";
import { Navigate, Link as RouterLink, useParams } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { copyText } from "../../lib/utils";
import { useSession } from "../../services/SessionContext";
import type { ShareLinkType } from "../../types/domain";

export function ShareUploadPage() {
  const { uploadId } = useParams();
  const { repo, createShareLink, disableShareLink } = useSession();
  if (!uploadId) return <Navigate to="/app/dashboard" replace />;
  const upload = repo.getUpload(uploadId);
  if (!upload) return <Navigate to="/app/dashboard" replace />;
  const currentUpload = upload;
  const links = repo.getShareLinks(currentUpload.uploadId);

  function make(type: ShareLinkType, hideSerialNumbers?: boolean) {
    createShareLink(currentUpload.uploadId, type, hideSerialNumbers);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 pb-20 md:pb-0">
      <div>
        <h1 className="text-2xl font-bold text-navy-950">Share this log</h1>
        <p className="mt-1 text-sm text-slate-600">{currentUpload.projectName || currentUpload.originalFileName}</p>
      </div>

      <div className="grid gap-4">
        <ShareTypeCard
          title="Public Link"
          description="Anyone with the link can view the readable timeline and summary."
          actionLabel="Create public link"
          onCreate={() => make("PUBLIC")}
        />
        <ShareTypeCard
          title="Client-Safe Link"
          description="Hides user information and serial numbers by default."
          actionLabel="Create client-safe link"
          onCreate={() => make("CLIENT_SAFE", true)}
        />
        <ShareTypeCard
          title="Support Link"
          description="Designed for Baseline support. Includes raw rows and parsed details."
          actionLabel="Create support link"
          onCreate={() => make("SUPPORT", false)}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Created links</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {links.length === 0 ? <p className="text-sm text-slate-600">No links created yet.</p> : null}
          {links.map((link) => {
            const path = link.type === "CLIENT_SAFE" ? `/c/${link.shareId}` : link.type === "SUPPORT" ? `/support/${link.shareId}` : `/s/${link.shareId}`;
            const href = `${window.location.origin}${path}`;
            return (
              <div key={link.shareId} className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold text-navy-900">{link.type.replace("_", " ")}</p>
                  <RouterLink className="inline-flex items-center gap-1 text-sm text-navy-900 hover:underline" to={path}>
                    {path} <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                  </RouterLink>
                  {link.hideSerialNumbers ? <p className="mt-1 text-xs text-slate-500">Serial numbers hidden</p> : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge className={link.isActive ? "bg-field-100 text-field-600 ring-field-600/20" : "bg-slate-100 text-slate-600 ring-slate-200"}>
                    {link.isActive ? "Active" : "Disabled"}
                  </Badge>
                  <Button size="sm" variant="secondary" onClick={() => copyText(href)}>
                    <Copy className="h-4 w-4" aria-hidden /> Copy link
                  </Button>
                  {link.isActive ? (
                    <Button size="sm" variant="destructive" onClick={() => disableShareLink(link.shareId)}>
                      Disable link
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function ShareTypeCard({
  title,
  description,
  actionLabel,
  onCreate,
}: {
  title: string;
  description: string;
  actionLabel: string;
  onCreate: () => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold text-navy-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-600">{description}</p>
        </div>
        <Button type="button" onClick={onCreate}>
          {actionLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
